import type {
  DialogContentProps,
  DialogDescriptionProps,
  DialogTitleProps,
} from "@kobalte/core/dialog";
import { Dialog as DialogPrimitive } from "@kobalte/core/dialog";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { IoArrowBack, IoClose } from "solid-icons/io";
import type { ComponentProps, ParentProps, ValidComponent } from "solid-js";
import { createMemo, mergeProps, Show, splitProps } from "solid-js";
import { cn } from "@/libs/cn";
import { withFocusReturn } from "./layer-focus";

export const Sheet = DialogPrimitive;
export const SheetTrigger = DialogPrimitive.Trigger;

/**
 * The slide is keyframes, not a transition, so `motion-reduce:transition-none`
 * would not touch it — reduced motion has to disable the animation instead.
 *
 * It has to keep the data-attribute in the stack. Reduced motion applied on its
 * own compiles to a lone class (specificity 0,1,0) while `data-expanded:animate-in`
 * compiles to `[data-expanded]` (0,2,0), and a media query adds no specificity —
 * so the unstacked form loses even though Tailwind emits it later.
 */
export const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-2 lg:p-6 shadow-lg transition-none ease-drawer data-expanded:animate-in data-closed:animate-out data-expanded:duration-200 data-closed:duration-200 motion-reduce:data-expanded:animate-none motion-reduce:data-closed:animate-none overflow-y-auto",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-closed:slide-out-to-top data-expanded:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-closed:slide-out-to-bottom data-expanded:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-closed:slide-out-to-left data-expanded:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-closed:slide-out-to-right data-expanded:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

type sheetContentProps<T extends ValidComponent = "div"> = ParentProps<
  DialogContentProps<T> &
    VariantProps<typeof sheetVariants> & {
      class?: string;
    }
>;

export const SheetContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, sheetContentProps<T>>,
) => {
  const merge = mergeProps<sheetContentProps<T>[]>({ side: "right" }, props);
  const [local, rest] = splitProps(merge as sheetContentProps, [
    "class",
    "children",
    "side",
    "onOpenAutoFocus",
    "onCloseAutoFocus",
  ]);
  const autoFocus = withFocusReturn(local);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        class={cn(
          "fixed inset-0 z-50 bg-background/80 data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0",
        )}
      />
      <DialogPrimitive.Content
        class={sheetVariants({ side: local.side, class: local.class })}
        onOpenAutoFocus={autoFocus.onOpenAutoFocus}
        onCloseAutoFocus={autoFocus.onCloseAutoFocus}
        {...rest}
      >
        {local.children}
        <DialogPrimitive.CloseButton class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-[opacity,box-shadow] hover:opacity-100 focus:outline-hidden focus:ring-[1.5px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="h-4 w-4"
          >
            <title>Close</title>
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M18 6L6 18M6 6l12 12"
            />
          </svg>
        </DialogPrimitive.CloseButton>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

type sheetTitleProps<T extends ValidComponent = "h2"> = DialogTitleProps<T> & {
  class?: string;
};

export const SheetTitle = <T extends ValidComponent = "h2">(
  props: PolymorphicProps<T, sheetTitleProps<T>>,
) => {
  const [local, rest] = splitProps(props as sheetTitleProps, ["class"]);

  return (
    <DialogPrimitive.Title
      class={cn(
        "text-lg font-display font-semibold text-foreground",
        local.class,
      )}
      {...rest}
    />
  );
};

type sheetDescriptionProps<T extends ValidComponent = "p"> =
  DialogDescriptionProps<T> & {
    class?: string;
  };

export const SheetDescription = <T extends ValidComponent = "p">(
  props: PolymorphicProps<T, sheetDescriptionProps<T>>,
) => {
  const [local, rest] = splitProps(props as sheetDescriptionProps, ["class"]);

  return (
    <DialogPrimitive.Description
      class={cn("text-sm text-muted-foreground", local.class)}
      {...rest}
    />
  );
};

export const SheetHeader = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        local.class,
      )}
      {...rest}
    />
  );
};

export const SheetFooter = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        local.class,
      )}
      {...rest}
    />
  );
};

// Fixed Sheet Components with fixed header/footer and scrollable content

export const FixedSheet = Sheet;
export const FixedSheetTrigger = SheetTrigger;

// Variant for fixed sheet content - removes overflow-y-auto and uses flex layout
// See `sheetVariants` above for why reduced motion needs the stacked
// `motion-reduce:data-*:animate-none` form rather than a bare `animate-none`.
export const fixedSheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-0 shadow-lg transition-none ease-drawer data-expanded:animate-in data-closed:animate-out data-expanded:duration-200 data-closed:duration-200 motion-reduce:data-expanded:animate-none motion-reduce:data-closed:animate-none flex flex-col h-full",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-closed:slide-out-to-top data-expanded:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-closed:slide-out-to-bottom data-expanded:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-closed:slide-out-to-left data-expanded:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-closed:slide-out-to-right data-expanded:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

type fixedSheetContentProps<T extends ValidComponent = "div"> = ParentProps<
  DialogContentProps<T> &
    VariantProps<typeof fixedSheetVariants> & {
      class?: string;
    }
>;

export const FixedSheetContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, fixedSheetContentProps<T>>,
) => {
  const merge = mergeProps<fixedSheetContentProps<T>[]>(
    { side: "right" },
    props,
  );
  const [local, rest] = splitProps(merge as fixedSheetContentProps, [
    "class",
    "children",
    "side",
    "onOpenAutoFocus",
    "onCloseAutoFocus",
  ]);
  const autoFocus = withFocusReturn(local);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        class={cn(
          "fixed inset-0 z-50 bg-background/80 data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0",
        )}
      />
      <DialogPrimitive.Content
        class={fixedSheetVariants({ side: local.side, class: local.class })}
        onOpenAutoFocus={autoFocus.onOpenAutoFocus}
        onCloseAutoFocus={autoFocus.onCloseAutoFocus}
        {...rest}
      >
        {local.children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

export const FixedSheetHeader = (
  props: ComponentProps<"div"> & { backType?: "close" | "back" },
) => {
  const [local, rest] = splitProps(props, [
    "class",
    "children",
    "backType",
    "classList",
  ]);

  const backType = createMemo(() => {
    return local.backType === "back" ? "back" : "close";
  });

  return (
    <div
      class={cn(
        "flex items-center justify-between bg-card border-b shrink-0 z-10 p-5 lg:p-6 relative",
        local.class,
      )}
      classList={{
        "flex-row-reverse justify-end gap-2": backType() === "back",
        ...local.classList,
      }}
      {...rest}
    >
      <div class="flex-1">{local.children}</div>
      <DialogPrimitive.CloseButton class="rounded-sm opacity-70 ring-offset-background transition-[opacity,box-shadow] hover:opacity-100 focus:outline-hidden focus:ring-[1.5px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none flex items-center justify-center h-5 w-5">
        <Show when={backType() === "back"} fallback={<IoClose />}>
          <IoArrowBack />
        </Show>
      </DialogPrimitive.CloseButton>
    </div>
  );
};

export const ScrollableContent = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex-1 overflow-y-auto overflow-x-hidden p-2 lg:p-6",
        local.class,
      )}
      {...rest}
    />
  );
};

export const FixedSheetFooter = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 bg-card border-t shrink-0 z-10 p-2 lg:p-6",
        local.class,
      )}
      {...rest}
    />
  );
};
